import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, AgentNumber, Campaign, DialList, StaffRecord } from "../lib/api";

export function CampaignsPage({ csrf }: { csrf: string }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [lists, setLists] = useState<DialList[]>([]);
  const [error, setError] = useState("");
  const [assignedNumbers, setAssignedNumbers] = useState<AgentNumber[]>([]);
  const [staff, setStaff] = useState<StaffRecord | null>(null);
  const [form, setForm] = useState({
    name: "",
    listId: "",
    messageScript:
      "Hello {{full_name}}, this is an important automated message. Please call us back when you can. Thank you.",
    maxConcurrent: 20,
    provider: "auto",
    ttsProvider: "auto",
    selectedOutboundNumberId: "",
    selectedReceivingMethod: "SIP" as "SIP" | "PERSONAL_PHONE",
    serviceContext: "MEGAMILLIONSCLAIMS",
    recordingEnabled: false,
    recordingConsent: false
  });

  async function refresh() {
    const [c, l] = await Promise.all([api.listCampaigns(), api.listLists()]);
    setCampaigns(c.campaigns);
    setLists(l.lists);
  }

  useEffect(() => {
    refresh().catch((e) => setError(e.message));
    api.getCampaignRouting().then((r) => {
      setAssignedNumbers((r.numbers || []).filter((n) => n.outboundEnabled !== false));
      setStaff(r.staff);
      setForm((f) => ({ ...f, selectedOutboundNumberId: f.selectedOutboundNumberId || r.numbers?.[0]?.id || "" }));
    }).catch((e) => setError(e.message));
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await api.createCampaign(
        {
          name: form.name,
          listId: form.listId || null,
          messageScript: form.messageScript,
          maxConcurrent: Number(form.maxConcurrent) || 20,
          provider: form.provider,
          ttsProvider: form.ttsProvider,
          selectedOutboundNumberId: form.selectedOutboundNumberId,
          selectedReceivingMethod: form.selectedReceivingMethod,
          serviceContext: form.serviceContext,
          recordingEnabled: form.recordingEnabled,
          recordingConsent: form.recordingConsent
        },
        csrf
      );
      setForm((f) => ({ ...f, name: "" }));
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Campaigns</h1>
          <p>Configure scripts and concurrency, then start multi-channel dialing.</p>
        </div>
      </div>
      {error ? <div className="alert error">{error}</div> : null}

      <div className="card">
        <h2>Your campaigns</h2>
        {campaigns.length === 0 ? (
          <p className="muted">No campaigns yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Channels</th>
                <th>Stats</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/campaigns/${c.id}`}>{c.name}</Link>
                  </td>
                  <td>
                    <span className={`badge ${c.status}`}>{c.status}</span>
                  </td>
                  <td>{c.maxConcurrent}</td>
                  <td className="muted">
                    ans {c.stats?.answered ?? 0} · done {c.stats?.completed ?? 0} · fail{" "}
                    {c.stats?.failed ?? 0}
                  </td>
                  <td>
                    <Link className="btn ghost sm" to={`/campaigns/${c.id}`}>
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>New campaign</h2>
        <form onSubmit={onCreate}>
          <div className="form-grid">
            <label>
              <strong>Name</strong>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="March outreach"
              />
            </label>
            <label>
              <strong>List (optional)</strong>
              <select value={form.listId} onChange={(e) => setForm({ ...form, listId: e.target.value })}>
                <option value="">All contacts</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <strong>Max concurrent calls</strong>
              <input
                type="number"
                min={1}
                max={80}
                value={form.maxConcurrent}
                onChange={(e) => setForm({ ...form, maxConcurrent: Number(e.target.value) })}
              />
            </label>
            <label>
              <strong>Telephony provider</strong>
              <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
                <option value="auto">Auto (PBX when ready)</option>
                <option value="pbx">FalconJA PBX / Twilio SIP</option>
                <option value="twilio">Twilio REST</option>
                <option value="telnyx">Telnyx</option>
              </select>
            </label>
            <label>
              <strong>TTS / AI voice</strong>
              <select
                value={form.ttsProvider}
                onChange={(e) => setForm({ ...form, ttsProvider: e.target.value })}
              >
                <option value="auto">Auto (ElevenLabs if key set)</option>
                <option value="elevenlabs">ElevenLabs</option>
                <option value="twilio">Provider native TTS</option>
                <option value="telnyx">Telnyx speak</option>
              </select>
            </label>
            <label>
              <strong>Service</strong>
              <select
                required
                value={form.serviceContext}
                onChange={(e) => setForm({ ...form, serviceContext: e.target.value })}
              >
                <option value="MEGAMILLIONSCLAIMS">MegaMillionsClaims</option>
                <option value="MEGAFED">Mega Federal Reserve</option>
              </select>
            </label>
            <label>
              <strong>Calling number</strong>
              <select required value={form.selectedOutboundNumberId} onChange={(e) => setForm({ ...form, selectedOutboundNumberId: e.target.value })}>
                <option value="">Select an assigned number</option>
                {assignedNumbers.map((n) => <option key={n.id} value={n.id}>{n.displayName || n.label || n.phone || n.e164}</option>)}
              </select>
            </label>
            <label>
              <strong>When customer presses 1, send call to</strong>
              <select value={form.selectedReceivingMethod} onChange={(e) => setForm({ ...form, selectedReceivingMethod: e.target.value as "SIP" | "PERSONAL_PHONE" })}>
                <option value="SIP">FalconPhone / SIP ({staff?.sipExtension || "not configured"})</option>
                <option value="PERSONAL_PHONE" disabled={!staff?.forwardingNumberVerified || staff?.forwardingEnabled === false}>My Cellphone ({staff?.forwardingNumberMasked || "not verified"})</option>
              </select>
            </label>
          </div>
          <label>
            <strong>Message script (spoken after answer)</strong>
            <textarea
              required
              value={form.messageScript}
              onChange={(e) => setForm({ ...form, messageScript: e.target.value })}
            />
            <span className="muted">
              Merge fields: {"{{full_name}}"}, {"{{first_name}}"}, {"{{last_name}}"}, {"{{company}}"},{" "}
              {"{{phone}}"}
            </span>
          </label>
          <label>
            <span>
              <input
                type="checkbox"
                checked={form.recordingEnabled}
                onChange={(e) => setForm({ ...form, recordingEnabled: e.target.checked })}
              />{" "}
              Record answered calls
            </span>
          </label>
          <label>
            <span>
              <input
                type="checkbox"
                checked={form.recordingConsent}
                onChange={(e) => setForm({ ...form, recordingConsent: e.target.checked })}
              />{" "}
              Recording disclosure / consent is in place for the jurisdictions being called
            </span>
          </label>
          <p className="muted">
            Recording only starts when both boxes are checked. DTMF 9 during the message opts the number out
            (DNC). DTMF 1 transfers only to this campaign's owner using the routing snapshot saved at dispatch.
          </p>
          <button className="btn" type="submit">
            Create campaign
          </button>
        </form>
      </div>
    </div>
  );
}
